<template>
  <main class="closest-page" :class="appStore.theme" :lang="appStore.language">
    <header>
      <h1>{{ englishGameName }}</h1>
      <div v-if="estimates.length" class="progress-wrap">
        <span>{{ t('closest_wins.round_progress', { current: roundIndex + 1, total: estimates.length }) }}</span>
        <div class="progress"><i :style="{ width: `${((roundIndex + 1) / estimates.length) * 100}%` }"></i></div>
      </div>
      <div
        class="timer"
        :class="{ urgent: phase === 'answering' && secondsLeft <= 5, review: phase === 'reveal', inactive: !['answering', 'reveal'].includes(phase) }"
      >{{ ['answering', 'reveal'].includes(phase) ? secondsLeft : '' }}</div>
    </header>

    <section v-if="loading" class="center-state"><span class="spinner"></span><p>{{ t('closest_wins.loading') }}</p></section>
    <section v-else-if="loadError || !currentEstimate" class="center-state error-state">
      <span class="state-icon">!</span>
      <h2>{{ t('closest_wins.no_estimates') }}</h2>
      <p>{{ loadError || t('closest_wins.check_data') }}</p>
    </section>

    <template v-else>
      <section class="round-stage">
        <div class="question-card">
          <span v-if="currentEstimate.category" class="category">{{ categoryLabel }}</span>
          <h2>{{ localized(currentEstimate.prompt) }}</h2>
          <div class="unit-pill">{{ t('closest_wins.answer_in', { unit: localized(currentEstimate.unit) }) }}</div>
        </div>

        <div v-if="phase === 'answering'" class="round-status">
          {{ t('closest_wins.answers_received', { answered: answerCount, total: activePlayers.length }) }}
        </div>

        <div v-else-if="phase === 'reveal'" class="reveal-card">
          <p>{{ t('closest_wins.correct_answer') }}</p>
          <strong>{{ formattedCorrectAnswer }}</strong>
          <p v-if="localized(currentEstimate.explanation)" class="explanation">{{ localized(currentEstimate.explanation) }}</p>
          <div v-if="roundWinners.length" class="round-winner">
            {{ roundWinnerText }}
          </div>
        </div>
      </section>

      <aside class="scoreboard">
        <h3>{{ t('closest_wins.scoreboard') }}</h3>
        <div v-for="(player, index) in rankedPlayers" :key="player.id" class="player-row" :style="{ '--player-colour': player.playerColour || '#5f718e' }">
          <span class="rank">{{ index + 1 }}</span>
          <div class="avatar-wrap">
            <img v-if="playerImage(player)" :src="playerImage(player)" class="avatar" alt="" />
            <span v-else class="avatar initials">{{ initials(playerName(player)) }}</span>
            <i class="connection-dot" :class="{ offline: player.connected === false, away: player.connected !== false && !player.inGame }" :style="{ backgroundColor: player.connected === false ? '#ff4d5e' : player.inGame === true ? '#40dc74' : '#ffad32' }"></i>
          </div>
          <div class="player-details">
            <strong>{{ playerName(player) }}</strong>
            <small v-if="phase === 'reveal' && hasAnswer(player.id)" class="estimate-result">
              <span class="estimate-value">{{ formattedPlayerAnswer(player.id) }}</span>
              <span>+{{ roundPoints[player.id] || 0 }} {{ t('closest_wins.points') }}</span>
            </small>
            <small v-else-if="phase === 'reveal'">{{ t('closest_wins.no_answer') }}</small>
            <small v-else>{{ hasAnswer(player.id) ? t('closest_wins.submitted') : t('closest_wins.thinking') }}</small>
          </div>
          <div class="player-score"><b>{{ scores[player.id] || 0 }}</b><small>{{ t('closest_wins.points') }}</small></div>
        </div>
      </aside>
    </template>

    <RoundCountdown v-if="phase === 'intermission'" :count="intermissionCount" />
    <section v-if="phase === 'finished'" class="result-overlay">
      <div class="fireworks"><i v-for="spark in 48" :key="spark" :style="confettiStyle(spark)"></i></div>
      <div class="result-card">
        <div class="trophy">★</div>
        <h2>{{ winners.length === 1 ? t('closest_wins.player_wins', { player: playerName(winners[0]) }) : t('closest_wins.players_tie', { players: winners.map(playerName).join(', ') }) }}</h2>
        <ol><li v-for="player in rankedPlayers" :key="player.id"><span>{{ playerName(player) }}</span><b>{{ scores[player.id] || 0 }}</b></li></ol>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import eventBus from '@/plugins/eventBus'
import { useAppStore } from '@/stores/appStore'
import { useWebsocketStore } from '@/stores/websocketStore'
import RoundCountdown from '@/components/RoundCountdown.vue'
import { soundManager, SOUNDS } from '@/services/soundManager'

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t } = useI18n()
const game = computed(() => appStore.room?.game || appStore.game || {})
const gamePlayers = computed(() => appStore.room?.game?.players || [])
// Presence drives the green/orange UI indicator, but it can briefly lag behind
// the controller screen. A selected player with a live socket must still be
// allowed to submit; room/min-player enforcement remains authoritative.
const activePlayers = computed(() => gamePlayers.value.filter(player => player.connected !== false))
const estimates = ref([])
const roundIndex = ref(0)
const phase = ref('loading')
const secondsLeft = ref(15)
const loading = ref(true)
const loadError = ref('')
const answersByPlayer = reactive({})
const scores = reactive({})
const roundPoints = reactive({})
const roundWinners = ref([])
const REVIEW_SECONDS = 5
const intermissionCount = ref(3)
const acceptedRequestIds = new Set()
let timer
let nextRoundTimer

const currentEstimate = computed(() => estimates.value[roundIndex.value] || null)
const englishGameName = computed(() => {
  const name = game.value?.name
  if (name && typeof name === 'object') return name.en || 'Closest Wins'
  return String(name || 'Closest Wins')
})
const answerCount = computed(() => {
  const activeIds = new Set(activePlayers.value.map(player => String(player.id)))
  return Object.keys(answersByPlayer).filter(playerId => activeIds.has(String(playerId))).length
})
const rankedPlayers = computed(() => [...gamePlayers.value].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)))
const topScore = computed(() => rankedPlayers.value.length ? (scores[rankedPlayers.value[0].id] || 0) : 0)
const winners = computed(() => rankedPlayers.value.filter(player => (scores[player.id] || 0) === topScore.value))
const categoryLabel = computed(() => String(currentEstimate.value?.category || '').replaceAll('_', ' '))
const formattedCorrectAnswer = computed(() => formatValue(currentEstimate.value?.answer, localized(currentEstimate.value?.unit)))
const roundWinnerText = computed(() => roundWinners.value.length === 1
  ? t('closest_wins.round_winner', { player: playerName(roundWinners.value[0]) })
  : t('closest_wins.round_tie', { players: roundWinners.value.map(playerName).join(', ') }))

function localized(value) {
  if (value == null) return ''
  if (typeof value !== 'object') return String(value)
  return value[appStore.language] || value.en || Object.values(value).find(entry => typeof entry === 'string') || ''
}
function playerName(player) { return player?.displayName || player?.name || player?.email || t('closest_wins.player') }
function playerImage(player) { return player?.avatar || player?.photoURL || player?.image || '' }
function initials(name) { return String(name).trim().split(/\s+/).map(word => word[0]).join('').slice(0, 2).toUpperCase() }
function shuffle(items) { const copy = [...items]; for (let i = copy.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [copy[i], copy[j]] = [copy[j], copy[i]] } return copy }
function difficultyValue(item) { return Number(item?.difficulty?.value ?? item?.difficulty) }
function isEligible(item) { return item?.active !== false && localized(item?.prompt).trim() && Number.isInteger(Number(item?.answer)) && localized(item?.unit).trim() }
function selectEstimates(items) {
  const eligible = items.filter(isEligible)
  const easy = shuffle(eligible.filter(item => [1, 2].includes(difficultyValue(item))))
  if (eligible.length < 10) throw new Error(t('closest_wins.not_enough_estimates', { available: eligible.length, required: 10 }))
  if (easy.length < 5) throw new Error(t('closest_wins.not_enough_easy', { available: easy.length, required: 5 }))
  const selected = easy.slice(0, 5)
  const ids = new Set(selected.map(item => item.id))
  const remainder = shuffle(eligible.filter(item => !ids.has(item.id))).slice(0, 5)
  return shuffle([...selected, ...remainder])
}
function datasetId() {
  const configured = game.value.dataTableId || game.value.content?.datasetIds?.[0] || game.value.datasetId
  return typeof configured === 'object' ? configured.value || configured.id : configured
}
async function loadEstimates() {
  loading.value = true
  loadError.value = ''
  try {
    const id = datasetId()
    if (!id) throw new Error(t('closest_wins.no_dataset'))
    const response = await fetch('https://getpublicdatatablecontent-ey37xij4wa-ew.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(id) })
    })
    if (response.status === 404) throw new Error(t('closest_wins.dataset_not_found'))
    if (!response.ok) throw new Error(t('closest_wins.load_failed'))
    const payload = await response.json()
    const items = Array.isArray(payload.items) ? payload.items : []
    estimates.value = selectEstimates(items.map((item, index) => ({ id: item.id || `${id}-${index}`, ...item })))
    startRound()
  } catch (error) {
    console.error('[ClosestWins] Failed to load estimates', error)
    loadError.value = error?.message || t('closest_wins.load_failed')
  } finally {
    loading.value = false
  }
}
function clearAnswers() { Object.keys(answersByPlayer).forEach(key => delete answersByPlayer[key]); Object.keys(roundPoints).forEach(key => delete roundPoints[key]) }
function startRound() {
  clearInterval(timer)
  clearInterval(nextRoundTimer)
  clearAnswers()
  roundWinners.value = []
  secondsLeft.value = 15
  phase.value = 'answering'
  soundManager.stopAll(); soundManager.play(SOUNDS.roundStart); soundManager.schedule(SOUNDS.quizCountdown, 450, { exclusiveCategory: true })
  timer = setInterval(() => {
    secondsLeft.value -= 1
    if (secondsLeft.value <= 0) revealRound(true)
  }, 1000)
}
function hasAnswer(playerId) { return Object.prototype.hasOwnProperty.call(answersByPlayer, playerId) }
function handleControl(control) {
  if (control?.requestId && acceptedRequestIds.has(control.requestId)) {
    acknowledgeControl(control, true)
    return
  }
  if (phase.value !== 'answering' || !['number-input', 'number', 'estimate'].includes(control?.type)) {
    acknowledgeControl(control, false)
    return
  }
  const player = activePlayers.value.find(item => item.id === control.userId)
  const value = Number(control.value)
  if (!player || hasAnswer(player.id) || !Number.isInteger(value)) {
    acknowledgeControl(control, false)
    return
  }
  answersByPlayer[player.id] = value
  if (control.requestId) acceptedRequestIds.add(control.requestId)
  acknowledgeControl(control, true)
  if (activePlayers.value.length && answerCount.value >= activePlayers.value.length) revealRound(false)
}
function acknowledgeControl(control, accepted) {
  if (!control?.requestId || !control?.userId) return
  socketStore.send({ event: 'control-result', data: { requestId: control.requestId, userId: control.userId, accepted } })
}
function revealRound(timedOut = false) {
  if (phase.value !== 'answering') return
  clearInterval(timer)
  soundManager.stopCategory('countdown')
  phase.value = 'reveal'
  const correct = Number(currentEstimate.value.answer)
  const submitted = activePlayers.value.filter(player => hasAnswer(player.id))
  if (submitted.length) {
    const closestDistance = Math.min(...submitted.map(player => Math.abs(answersByPlayer[player.id] - correct)))
    roundWinners.value = submitted.filter(player => Math.abs(answersByPlayer[player.id] - correct) === closestDistance)
    roundWinners.value.forEach(player => { roundPoints[player.id] = 100; scores[player.id] = (scores[player.id] || 0) + 100 })
  }
  soundManager.play(timedOut ? SOUNDS.timesUp : (submitted.length ? SOUNDS.correct : SOUNDS.wrong))
  if (roundWinners.value.length) soundManager.schedule(SOUNDS.points, 650)
  activePlayers.value.forEach(player => { if (!(player.id in roundPoints)) roundPoints[player.id] = 0 })
  secondsLeft.value = REVIEW_SECONDS
  nextRoundTimer = setInterval(() => {
    secondsLeft.value -= 1
    if (secondsLeft.value > 0) return
    clearInterval(nextRoundTimer)
    if (roundIndex.value + 1 >= estimates.value.length) finishGame()
    else startIntermission()
  }, 1000)
}
function startIntermission() {
  soundManager.play(SOUNDS.nextRoundCountdown, { exclusiveCategory: true })
  phase.value = 'intermission'; intermissionCount.value = 3
  nextRoundTimer = setInterval(() => {
    intermissionCount.value -= 1
    if (intermissionCount.value > 0) return
    clearInterval(nextRoundTimer)
    roundIndex.value += 1
    startRound()
  }, 1000)
}
function finishGame() {
  soundManager.stopAll(); soundManager.play(SOUNDS.winner)
  phase.value = 'finished'
  socketStore.send({ event: 'finished', data: {
    gameId: appStore.game?.id,
    scores: { ...scores },
    winnerIds: winners.value.map(player => player.id),
    rounds: estimates.value.map(item => item.id)
  } })
}
function formatValue(value, unit = '') { return `${Number(value).toLocaleString(appStore.language === 'el' ? 'el-GR' : 'en-GB')} ${unit}`.trim() }
function formattedPlayerAnswer(playerId) { return formatValue(answersByPlayer[playerId], localized(currentEstimate.value?.unit)) }
function restartGame() {
  roundIndex.value = 0
  Object.keys(scores).forEach(key => { scores[key] = 0 })
  clearAnswers()
  loadEstimates()
}
function confettiStyle(spark) { return { '--x': `${(spark * 37) % 100}%`, '--delay': `${(spark % 12) * -.18}s`, '--drift': `${((spark * 29) % 180) - 90}px`, '--colour': `hsl(${(spark * 43) % 360},90%,62%)` } }

onMounted(() => {
  gamePlayers.value.forEach(player => { scores[player.id] = 0 })
  eventBus.on('game-control', handleControl)
  eventBus.on('restart-round', restartGame)
  socketStore.send({ event: 'playing', data: { gameId: game.value.id } })
  loadEstimates()
})
onBeforeUnmount(() => {
  clearInterval(timer)
  clearInterval(nextRoundTimer)
  soundManager.stopAll()
  eventBus.off('game-control', handleControl)
  eventBus.off('restart-round', restartGame)
})
</script>

<style scoped>
.closest-page{height:100vh;box-sizing:border-box;overflow:hidden;padding:2.5vh 2.5vw;display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,30vw);grid-template-rows:clamp(68px,11vh,96px) minmax(0,1fr);gap:2vh 2vw;color:#fff;background:radial-gradient(circle at 22% 12%,#4c346f,#171426 44%,#090811)}.closest-page.light{color:#202035;background:#f5f0fb}header{grid-column:1/-1;height:100%;min-height:0;display:flex;align-items:center;gap:2vw}h1{margin:0;font-size:clamp(1.5rem,4vh,2.8rem)}.progress-wrap{margin-left:auto;width:min(32vw,420px);font-weight:800}.progress{height:8px;margin-top:.7vh;overflow:hidden;border-radius:20px;background:#ffffff25}.progress i{display:block;height:100%;background:#e5a5ff}.timer{width:clamp(3.4rem,8vh,5rem);height:clamp(3.4rem,8vh,5rem);min-width:clamp(3.4rem,8vh,5rem);min-height:clamp(3.4rem,8vh,5rem);box-sizing:border-box;flex:0 0 clamp(3.4rem,8vh,5rem);display:grid;place-items:center;border:4px solid #e5a5ff;border-radius:50%;background:#302142;font-size:clamp(1.4rem,4vh,2.4rem);font-weight:900}.timer.inactive{visibility:hidden}.timer.urgent{border-color:#ff6575;color:#ff8f9b}.round-stage{min-width:0;display:flex;flex-direction:column;gap:2vh}.question-card,.reveal-card{padding:3vh 3vw;border:1px solid #ffffff20;border-radius:24px;background:#ffffff0d;text-align:center}.question-card{min-height:31vh;display:flex;flex-direction:column;align-items:center;justify-content:center}.question-card h2{max-width:1050px;margin:1.2vh 0;font-size:clamp(1.65rem,4.8vh,3.6rem);line-height:1.15}.category{text-transform:capitalize;color:#e4adff;font-weight:800}.unit-pill{margin-top:1.5vh;padding:.65rem 1.1rem;border-radius:999px;background:#e5a5ff;color:#271331;font-weight:900}.round-status{text-align:center;font-size:clamp(1rem,2.5vh,1.5rem);font-weight:800}.reveal-card>p:first-child{margin:0;text-transform:uppercase;letter-spacing:.1em;opacity:.7}.reveal-card>strong{display:block;margin:.5vh 0;font-size:clamp(2.2rem,7vh,5rem);color:#f4c2ff}.explanation{margin:1vh auto;max-width:900px;font-size:clamp(.85rem,2.1vh,1.25rem)}.round-winner{margin-top:1.5vh;font-size:clamp(1rem,2.7vh,1.6rem);font-weight:900}.scoreboard{padding:2vh 1.2vw;overflow:hidden;border:1px solid #ffffff18;border-radius:22px;background:#ffffff0b}.scoreboard h3{margin:0 0 1.5vh}.player-row{display:grid;grid-template-columns:auto auto minmax(0,1fr) auto;align-items:center;gap:.7vw;padding:1vh .5vw;border-left:4px solid var(--player-colour);border-bottom:1px solid #ffffff16}.rank{font-weight:900;color:#e4adff}.avatar-wrap{position:relative}.avatar{width:clamp(2rem,5vh,3rem);aspect-ratio:1;border-radius:50%;object-fit:cover}.initials{display:grid;place-items:center;background:#563b70;font-weight:900}.connection-dot{position:absolute;right:-2px;bottom:1px;width:.7rem;height:.7rem;border:2px solid #21162d;border-radius:50%;background:#40dc74}.connection-dot.away{background:#ffad32}.connection-dot.offline{background:#ff4d5e}.player-details{min-width:0;display:flex;flex-direction:column}.player-details strong,.player-details small{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.player-details small{opacity:.7}.player-score{min-width:3.2rem;padding:.45rem .55rem;border-radius:.7rem;background:#e5a5ff20;text-align:center}.player-score b{display:block;font-size:clamp(1.1rem,3vh,1.65rem);line-height:1}.player-score small{display:block;margin-top:.2rem;font-size:.65rem;text-transform:uppercase;opacity:.7}.center-state{grid-column:1/-1;display:grid;place-items:center;align-content:center;text-align:center}.spinner{width:4rem;aspect-ratio:1;border:6px solid #ffffff24;border-top-color:#e5a5ff;border-radius:50%;animation:spin .8s linear infinite}.state-icon{width:4rem;aspect-ratio:1;display:grid;place-items:center;border-radius:50%;background:#ff5c67;font-size:2.5rem}.result-overlay{position:fixed;inset:0;z-index:50;display:grid;place-items:center;background:#090611e8}.result-card{position:relative;z-index:2;width:min(75vw,650px);padding:4vh 4vw;border-radius:28px;background:#fff;color:#21152b;text-align:center}.trophy{font-size:clamp(4rem,12vh,8rem);color:#d782ff}.result-card h2{margin:0 0 2vh;font-size:clamp(2rem,5vh,3.7rem)}.result-card ol{max-width:450px;margin:auto;padding:0;list-style:none}.result-card li{display:flex;justify-content:space-between;padding:1vh;border-bottom:1px solid #e7ddec}.fireworks{position:absolute;inset:0;overflow:hidden}.fireworks i{position:absolute;left:var(--x);top:-5vh;width:.65rem;height:1rem;background:var(--colour);animation:fall 3s linear infinite;animation-delay:var(--delay)}.light .question-card,.light .reveal-card,.light .scoreboard{border-color:#ddd2e8;background:#fff}.light .timer{background:#fff}.light .reveal-card>strong{color:#743a91}@keyframes spin{to{transform:rotate(360deg)}}@keyframes fall{to{transform:translateX(var(--drift)) translateY(115vh) rotate(780deg);opacity:.15}}
.timer.review{border-color:#80caff;color:#80caff}

/* Keep estimation rounds visually consistent with the compact quiz layout. */
.closest-page{padding:2vh 2vw;grid-template-columns:minmax(0,1fr) minmax(250px,28vw);grid-template-rows:clamp(62px,10vh,88px) minmax(0,1fr);gap:1.5vh 1.5vw}
header{gap:1.5vw}
h1{font-size:clamp(1.25rem,3vh,2.1rem);line-height:1.05}
.progress-wrap{width:min(30vw,380px);font-size:clamp(.7rem,1.6vh,.95rem);font-weight:700}
.progress{height:7px;margin-top:.5vh}
.timer{width:clamp(3rem,7vh,4.4rem);height:clamp(3rem,7vh,4.4rem);min-width:clamp(3rem,7vh,4.4rem);min-height:clamp(3rem,7vh,4.4rem);flex-basis:clamp(3rem,7vh,4.4rem);font-size:clamp(1.2rem,3vh,2rem)}
.round-stage{gap:1.3vh}
.question-card,.reveal-card{padding:2vh 1.7vw;border-radius:20px}
.question-card{min-height:18vh}
.question-card h2{max-width:900px;margin:.7vh 0;font-size:clamp(1.05rem,3.15vh,2.15rem);line-height:1.14;overflow-wrap:anywhere}
.category{font-size:clamp(.68rem,1.5vh,.9rem)}
.unit-pill{margin-top:.8vh;padding:.42rem .8rem;font-size:clamp(.68rem,1.55vh,.9rem)}
.round-status{margin:0;text-align:center;font-size:clamp(.72rem,1.75vh,1rem);font-weight:700}
.reveal-card>strong{font-size:clamp(1.7rem,5vh,3.3rem)}
.explanation{font-size:clamp(.72rem,1.65vh,.98rem);line-height:1.25}
.round-winner{margin-top:.8vh;font-size:clamp(.82rem,2vh,1.2rem)}
.scoreboard{min-width:0;padding:1.5vh 1vw;border-radius:20px}
.scoreboard h3{margin:0 0 1vh;font-size:clamp(.9rem,2vh,1.15rem)}
.player-row{gap:.55vw;padding:.8vh .35vw}
.rank{font-size:clamp(.72rem,1.7vh,.95rem)}
.avatar{width:clamp(1.7rem,4.5vh,2.7rem);height:clamp(1.7rem,4.5vh,2.7rem)}
.player-details strong{font-size:clamp(.7rem,1.6vh,.95rem)}
.player-details small{font-size:clamp(.6rem,1.35vh,.78rem)}
.player-details .estimate-result{display:flex;flex-wrap:wrap;gap:.1rem .45rem;overflow:visible;white-space:normal;line-height:1.2;opacity:1}
.estimate-value{color:#f4c2ff;font-weight:800}
.light .estimate-value{color:#743a91}
.player-score{min-width:3rem;padding:.4rem}
</style>
